import { useCurrencyNumberFormatter } from 'hooks';
import useMemberSettings from 'hooks/models/member/useMemberSettings';
import useHasCostPermission from 'hooks/permission/useHasCostPermission';
import useMultiEquipmentTrackingEnabled from 'hooks/settings/useMultiEquipmentTrackingEnabled/useMultiEquipmentTrackingEnabled';
import { keyBy, sortBy } from 'lodash';
import { useMemo } from 'react';
import {
  defaultActivityReportsColumns,
  defaultEquipmentActivityReportsColumns,
} from 'store/activityReports/ActivityReports';
import { IVisibleTableColumn } from 'types/TableColumn';
import { mergeListsWithOverride } from 'utils/collectionUtils';
import { secondsToHmmString } from 'utils/dateUtils';
import { t } from 'utils/localize';
import { IActivityReportRowInfo } from './ActivityReportData';

export interface IActivityReportExportColumn {
  key: string;
  label: string;
  valueFormatter: (val: number) => string;
}

const decimalHoursFormatter = (val: number) => (val / 3600).toFixed(2);

const defaultActivityReportsExportTimeColumns: IActivityReportExportColumn[] = [
  { key: 'regularHours', label: t('Regular Time'), valueFormatter: secondsToHmmString },
  { key: 'regularHoursDec', label: t('Regular Time (Decimal)'), valueFormatter: decimalHoursFormatter },
  { key: 'overtimeHours', label: t('Overtime'), valueFormatter: secondsToHmmString },
  { key: 'overtimeHoursDec', label: t('Overtime (Decimal)'), valueFormatter: decimalHoursFormatter },
  { key: 'doubleTimeHours', label: t('Double Time'), valueFormatter: secondsToHmmString },
  { key: 'doubleTimeHoursDec', label: t('Double Time (Decimal)'), valueFormatter: decimalHoursFormatter },
  { key: 'totalHours', label: t('Total'), valueFormatter: secondsToHmmString },
  { key: 'totalHoursDec', label: t('Total (Decimal)'), valueFormatter: decimalHoursFormatter },
];

const defaultEquipmentActivityReportsExportAddOnColumns: IActivityReportExportColumn[] = [
  { key: 'laborHours', label: t('Labor Hours'), valueFormatter: secondsToHmmString },
  { key: 'laborHoursDec', label: t('Labor Hours (Decimal)'), valueFormatter: decimalHoursFormatter },
  { key: 'addOnHours', label: t('Add-On Hours'), valueFormatter: secondsToHmmString },
  { key: 'addOnHoursDec', label: t('Add-On Hours (Decimal)'), valueFormatter: decimalHoursFormatter },
];

/**
 * Keys of columns the member isn't allowed/able to see, mirroring the on-screen table's
 * own visibility rules (cost columns require cost permission, add-on/labor-hours columns
 * require multi-equipment tracking).
 */
function getUnavailableColumnKeys(
  includeEquipmentColumns: boolean,
  canViewCost: boolean,
  hasMultiEquipmentTracking: boolean
): string[] {
  const unavailableColumnKeys: string[] = [];

  if (!canViewCost) {
    unavailableColumnKeys.push('regularCost', 'overtimeCost', 'doubletimeCost', 'totalCost', 'laborBurden');
    if (includeEquipmentColumns) {
      unavailableColumnKeys.push('equipmentRate', 'equipmentCost', 'equipmentTotalCost');
    }
  }
  if (includeEquipmentColumns && !hasMultiEquipmentTracking) {
    unavailableColumnKeys.push('laborHours', 'laborHoursDec', 'addOnHours', 'addOnHoursDec');
  }

  return unavailableColumnKeys;
}

/**
 * Builds the ordered, visibility-filtered column list (from the member's saved column
 * settings) and the label/formatter definitions used to render each export field.
 */
export default function useActivityReportExportColumns(includeEquipmentColumns: boolean) {
  const memberSettings = useMemberSettings();
  const costFormatter = useCurrencyNumberFormatter('usd', { maximumFractionDigits: 2 })!;
  const canViewCost = useHasCostPermission();
  const hasMultiEquipmentTracking = useMultiEquipmentTrackingEnabled();

  const orderedColumns = useMemo(() => {
    const savedColumns = includeEquipmentColumns
      ? memberSettings?.web?.features?.activityReport?.equipmentColumnSettings
      : memberSettings?.web?.features?.activityReport?.columnSettings;
    const defaultColumns = includeEquipmentColumns
      ? defaultEquipmentActivityReportsColumns
      : defaultActivityReportsColumns;

    const merged = mergeListsWithOverride(defaultColumns, savedColumns ?? [], 'key', ['position', 'visible']);
    const unavailableColumnKeys = getUnavailableColumnKeys(
      includeEquipmentColumns,
      canViewCost,
      hasMultiEquipmentTracking
    );

    return sortBy(
      merged.filter((column) => !unavailableColumnKeys.includes(column.key)),
      (column) => column.position ?? 0
    );
  }, [memberSettings, includeEquipmentColumns, canViewCost, hasMultiEquipmentTracking]);

  const costExportColumns: IActivityReportExportColumn[] = useMemo(() => {
    const costValueFormatter = (val: number) => costFormatter.format(val);

    if (includeEquipmentColumns) {
      return [
        { key: 'regularCost', label: t('Regular Cost (Labor)'), valueFormatter: costValueFormatter },
        { key: 'overtimeCost', label: t('Overtime Cost (Labor)'), valueFormatter: costValueFormatter },
        { key: 'doubletimeCost', label: t('Double Time Cost (Labor)'), valueFormatter: costValueFormatter },
        { key: 'laborBurden', label: t('Labor Burden'), valueFormatter: costValueFormatter },
        { key: 'totalCost', label: t('Total Cost (Labor)'), valueFormatter: costValueFormatter },
        { key: 'equipmentRate', label: t('Equip. Rate'), valueFormatter: costValueFormatter },
        { key: 'equipmentCost', label: t('Equip. Cost'), valueFormatter: costValueFormatter },
        { key: 'equipmentTotalCost', label: t('Total Cost'), valueFormatter: costValueFormatter },
      ];
    }

    return [
      { key: 'regularCost', label: t('Regular Cost'), valueFormatter: costValueFormatter },
      { key: 'overtimeCost', label: t('Overtime Cost'), valueFormatter: costValueFormatter },
      { key: 'doubletimeCost', label: t('Double Time Cost'), valueFormatter: costValueFormatter },
      { key: 'laborBurden', label: t('Labor Burden'), valueFormatter: costValueFormatter },
      { key: 'totalCost', label: t('Total Cost'), valueFormatter: costValueFormatter },
    ];
  }, [costFormatter, includeEquipmentColumns]);

  const exportColumnsByKey = useMemo(() => {
    const columns = includeEquipmentColumns
      ? [
          ...defaultActivityReportsExportTimeColumns,
          ...defaultEquipmentActivityReportsExportAddOnColumns,
          ...costExportColumns,
        ]
      : [...defaultActivityReportsExportTimeColumns, ...costExportColumns];

    return keyBy(columns, 'key');
  }, [costExportColumns, includeEquipmentColumns]);

  return { orderedColumns, exportColumnsByKey };
}

function getColumnRawValue(key: string, row: IActivityReportRowInfo, total: number): number {
  switch (key) {
    case 'totalHours':
    case 'totalHoursDec':
      return total;
    case 'laborHours':
      return row.totalHours;
    case 'laborHoursDec':
      return row.totalHoursDec;
    case 'addOnHours':
    case 'addOnHoursDec':
      return row.addOnTime;
    default:
      return (row as unknown as Record<string, number>)[key];
  }
}

/**
 * Builds the export fields for a single row, in column-settings order, respecting visibility.
 */
export function buildActivityReportExportColumnsData(
  orderedColumns: IVisibleTableColumn[],
  exportColumnsByKey: Record<string, IActivityReportExportColumn>,
  row: IActivityReportRowInfo,
  total: number
): Record<string, string> {
  return orderedColumns.reduce<Record<string, string>>((acc, column) => {
    const exportColumn = exportColumnsByKey[column.key];
    if (!column.visible || !exportColumn) {
      return acc;
    }

    acc[exportColumn.label] = exportColumn.valueFormatter(getColumnRawValue(column.key, row, total));
    return acc;
  }, {});
}
